import type {
  ExportPhotoReportBody,
  PhotoReportCoverRowBody,
  PhotoReportExportOptionsBody,
  PhotoReportPhotoRefBody,
  PhotoReportPhotoRefBodySource,
  PhotoReportRoomGroupBody,
} from "@rebuild/shared/models";
import type { PhotoGalleryGroup, PhotoGalleryItem } from "../../details-section/Photos/photos.types";

/** Per-photo preview state: whether it will be exported and its (export-only) caption. */
export interface PhotoSelectionEntry {
  included: boolean;
  caption: string;
}

/** galleryId → selection state. A missing entry is treated as included (defensive default). */
export type PhotoSelectionState = Map<string, PhotoSelectionEntry>;

/** group.id → user-edited room title. A missing entry means "use the group's own title". */
export type RoomTitleState = Map<string, string>;

/**
 * The room heading shown in the preview, TOC, and PDF for a group — the user's override when set,
 * otherwise the group's own title, falling back to "Photos". Kept in one place so the preview,
 * table of contents, and export request stay in lockstep.
 */
export function getRoomTitle(group: PhotoGalleryGroup, roomTitles: RoomTitleState): string {
  const override = roomTitles.get(group.id);
  const resolved = override !== undefined ? override : (group.title ?? "");
  return resolved.trim() || "Photos";
}

/** True unless the photo was explicitly excluded. */
export function isPhotoIncluded(selection: PhotoSelectionState, galleryId: string): boolean {
  return selection.get(galleryId)?.included !== false;
}

/**
 * Resolve the opaque server-facing reference (source + id) for a gallery item. Returns null when
 * the item can't be exported (an estimate file still uploading has no `uploadId`), matching how
 * `normalizeEstimateFilePhoto` already drops such files from the gallery.
 */
export function getPhotoRef(item: PhotoGalleryItem): { source: PhotoReportPhotoRefBodySource; id: string } | null {
  if (item.source === "estimate-file") {
    return item.file?.uploadId ? { source: "estimate-file", id: item.file.uploadId } : null;
  }
  return item.responsiveNotePhoto?.id ? { source: "responsive-note", id: item.responsiveNotePhoto.id } : null;
}

/** Every exportable gallery item across all groups, in display (= PDF) order. */
export function getExportablePhotos(groups: PhotoGalleryGroup[]): PhotoGalleryItem[] {
  return groups.flatMap((group) => group.photos.filter((photo) => getPhotoRef(photo) !== null));
}

/**
 * Map the preview's live state to the `ExportPhotoReportBody` wire shape: display order,
 * included photos only, empty rooms omitted, captions only when the option is on. The viewer's
 * timezone is sent so the server renders label timestamps in the same zone the user sees (mirrors
 * the `downloadScopeNotes` precedent).
 */
export function buildPhotoReportExportRequest(
  groups: PhotoGalleryGroup[],
  selection: PhotoSelectionState,
  options: PhotoReportExportOptionsBody,
  coverRows: PhotoReportCoverRowBody[],
  roomTitles: RoomTitleState,
): ExportPhotoReportBody {
  const roomGroups: PhotoReportRoomGroupBody[] = [];

  for (const group of groups) {
    const photos: PhotoReportPhotoRefBody[] = [];
    for (const item of group.photos) {
      if (!isPhotoIncluded(selection, item.galleryId)) {
        continue;
      }
      const ref = getPhotoRef(item);
      if (!ref) {
        continue;
      }
      const caption = options.includeCaptions ? selection.get(item.galleryId)?.caption?.trim() : undefined;
      photos.push(caption ? { ...ref, caption } : ref);
    }

    if (photos.length > 0) {
      roomGroups.push({ roomTitle: getRoomTitle(group, roomTitles), photos });
    }
  }

  return {
    roomGroups,
    options,
    // Only send cover rows when the cover page is on — otherwise they're irrelevant to the PDF.
    coverRows: options.includeCoverPage ? coverRows : undefined,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
